/**
 * "free" slide: freely placed text / image / sound / video elements, optionally
 * linking to another slide.
 */
import type { FreeElement, SlideOf } from "@makeit/schema";
import { layoutStyle } from "../Stage";
import { ElementImage, TextBox, useElementSound } from "../elements";
import { usePlayer } from "../context";

export function FreeSlide({ slide }: { slide: SlideOf<"free"> }) {
  return (
    <>
      {slide.freeElements.map((el) => (
        <FreeElementView key={el.id} element={el} />
      ))}
    </>
  );
}

export function FreeElementView({ element }: { element: FreeElement }) {
  const { goToSlide } = usePlayer();
  const { play, hasSound } = useElementSound(element);
  const interactive = hasSound || element.link !== null || element.kind === "video";

  const onClick = () => {
    if (hasSound) play();
    if (element.link !== null) goToSlide(element.link);
  };

  const style = { ...layoutStyle(element.layout), cursor: interactive ? "pointer" : undefined };

  switch (element.kind) {
    case "text":
      return (
        <div className="free-element free-text" style={style} onClick={interactive ? onClick : undefined}>
          <TextBox text={element.text} bestFit={300} />
        </div>
      );
    case "video":
      return (
        <div className="free-element free-video" style={style}>
          <ElementImage element={element} />
          {element.url && <VideoEmbed url={element.url} />}
        </div>
      );
    case "sound":
    case "image":
    default:
      return (
        <div className="free-element free-image" style={style} onClick={interactive ? onClick : undefined}>
          <ElementImage element={element} />
          {element.kind === "sound" && <span className="sound-badge" aria-hidden>♪</span>}
        </div>
      );
  }
}

function VideoEmbed({ url }: { url: string }) {
  const id = youtubeId(url);
  if (!id) return null;
  return (
    <iframe
      className="video-embed"
      src={`https://www.youtube-nocookie.com/embed/${id}?rel=0&modestbranding=1`}
      title="video"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }}
    />
  );
}

/**
 * The YouTube video id of a free element's `url`. The app stores the bare id
 * (MenuElements.OnVideoSelected); older web pages stored the whole link.
 */
export function youtubeId(url: string): string | null {
  const u = url.trim();
  if (/^[A-Za-z0-9_-]{11}$/.test(u)) return u;
  const m = u.match(/(?:youtu\.be\/|youtube(?:-nocookie)?\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/|v\/)|[?&]v=)([A-Za-z0-9_-]{11})/);
  return m ? m[1] : null;
}
